import EZFileLogo from '@/assets/img/ez-file-logo';
import { stytch } from '@/auth/stytch';
import { PrimaryButton } from '@/components/PrimaryButton';
import { StepForm } from '@/components/StepForm';
import { ArrowUpward } from '@/components/icons/ArrowUpward';
import { BillSize, FaAnswers } from '@/state/state.types';
import { DASHBOARD_ROUTES, getDashboardURL } from '@/utils/dashboardUrl';
import { Anchor, Button, Card, Flex, Group, Image, MantineTheme, Text } from '@mantine/core';
import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { useFields } from './hooks/useFields';

interface PartnershipConfig {
  card: {
    bg: string;
  };
  content: {
    headerText: string | null;
    descriptionText: string;
    primaryButtonText: React.ReactNode;
    primaryButtonStyles: Record<string, any>;
    rejectedButtonArrowColor: string;
    bottomButtonText: string;
    ezFileLink: string;
  };
}

interface FinancialAidStepProps {
  isPartnershipPreset: boolean;
  partnership?: string;
  logoSrc?: string;
  firstName: string;
  lastName: string;
  isLoading: boolean;
  setAnswer: Dispatch<SetStateAction<'' | FaAnswers>>;
  theme: MantineTheme;
}

const redirectToEzFileDashboard = () => {
  const tokens = stytch.session.getTokens();

  return `${getDashboardURL()}/${DASHBOARD_ROUTES.sessionAuthenticate}?sessionToken=${
    tokens?.session_token ?? ''
  }&ezFile=true`;
};

// Component with full TypeScript support
const FinancialAidStep: React.FC<FinancialAidStepProps> = ({
  isPartnershipPreset,
  partnership,
  logoSrc,
  firstName,
  lastName,
  isLoading,
  setAnswer,
  theme,
}) => {
  const fields = useFields();
  const isLessThan5k = BillSize.Values['less-than-5k'] === fields?.['bill-size'];

  // Memoized configuration with proper typing
  const config: PartnershipConfig = useMemo(
    () => ({
      card: {
        bg: isPartnershipPreset ? 'primary.0' : 'tertiary.0',
      },
      content: {
        headerText: isPartnershipPreset ? null : `EZ-File for ${firstName} ${lastName}`,
        descriptionText: isPartnershipPreset
          ? `With ${
              partnership || 'our partner'
            } you receive free access to EZ File, a 15 minute solution to get you access to financial aid. Fill out the EZ File question form, and we'll take care of the rest!`
          : 'We have a service that applies for financial aid on your behalf. This should significantly decrease or eliminate your medical bill. Click on the link below to get started.',
        primaryButtonText: isPartnershipPreset ? (
          <Flex align="start">
            <Text mr={8}>Continue with</Text>
            <EZFileLogo
              width={87}
              flat
            />
          </Flex>
        ) : (
          'Take me to EZ-File'
        ),
        primaryButtonStyles: isPartnershipPreset
          ? {
              bg: 'tertiary.3',
              styles: (theme: MantineTheme) => ({
                label: { color: theme.colors.neutral[6] },
              }),
              color: 'neutral.6',
            }
          : {},
        rejectedButtonArrowColor: isPartnershipPreset
          ? theme.colors.neutral[6]
          : theme.colors.secondary[0],
        bottomButtonText: isPartnershipPreset
          ? 'No thanks, take me to Resolve Concierge'
          : "I'd rather Resolve work on my behalf",
        ezFileLink: redirectToEzFileDashboard(),
      },
    }),
    [isPartnershipPreset, partnership, firstName, lastName, theme],
  );

  // Event handlers with proper typing
  const handlePrimaryAction = (): void => {
    setAnswer(FaAnswers.yes);
  };

  const handleRejectedAction = (): void => {
    setAnswer(FaAnswers.rejected);
  };

  const handleBottomAction = (): void => {
    setAnswer(FaAnswers.no);
  };

  return (
    <StepForm>
      <Card
        bg={config.card.bg}
        radius={12}
        w="100%"
        p={26}
      >
        {/* Header Section */}
        <Flex
          w="100%"
          justify="space-between"
          align="center"
          mx="auto"
          direction="row"
          mb={18}
        >
          {isPartnershipPreset && <EZFileLogo />}
          {isPartnershipPreset && logoSrc && (
            <Flex
              justify="between"
              align="end"
              direction="row"
            >
              <Text
                size={11}
                c="white"
                fw={400}
                display="flex"
              >
                Included with
              </Text>
              <Image
                src={logoSrc}
                alt={`${partnership || 'Partner'} logo`}
                style={{
                  width: 100,
                  marginLeft: 10,
                }}
              />
            </Flex>
          )}
        </Flex>

        {/* Optional Header Text */}
        {config.content.headerText && (
          <Text
            size={18}
            c="white"
            fw={700}
            display="flex"
            mb={15}
          >
            {config.content.headerText}
          </Text>
        )}

        {/* Main Qualification Text */}
        <Text
          size={22}
          c="white"
          fw={700}
          display="flex"
        >
          You&nbsp;
          <Text
            size={22}
            c={isPartnershipPreset ? 'primary.2' : 'minor.0'}
            fw={700}
            display="contents"
            {...(isPartnershipPreset && { fs: 'italic' as const })}
          >
            should
          </Text>
          &nbsp;qualify for Financial Aid!
        </Text>

        {/* Qualification Description */}
        <Text
          size={16}
          c="white"
          mt={10}
        >
          Based on the income and household size you shared, you should qualify for Financial Aid
          from the hospital.
        </Text>

        {/* Service Description */}
        <Text
          size={16}
          c={isPartnershipPreset ? 'minor.1' : 'white'}
          mt={10}
          display="flex"
        >
          {config.content.descriptionText}
        </Text>

        {/* Action Buttons */}
        <Flex
          w="100%"
          justify="center"
          align="center"
          mx="auto"
          mt={25}
          direction="column"
        >
          <Anchor
            href={config.content.ezFileLink}
            target="_blank"
            fz="sm"
            maw={343}
            w="100%"
          >
            <PrimaryButton
              loading={isLoading}
              type="button"
              onClick={handlePrimaryAction}
              w="100%"
              mt={{ base: 12, md: 0 }}
              {...config.content.primaryButtonStyles}
            >
              {config.content.primaryButtonText}
            </PrimaryButton>
          </Anchor>

          <Button
            type="button"
            onClick={handleRejectedAction}
            mt={20}
            ml={10}
            mr={10}
            loading={isLoading}
            w={{ base: '100%', xs: 200, sm: '100%', md: '100%' }}
            sx={{
              root: {
                backgroundColor: 'transparent',
                border: 'none',
              },
            }}
            p={0}
          >
            <Group spacing={2}>
              <Flex
                align="center"
                justify="center"
                wrap="wrap"
              >
                <Text
                  mr={4}
                  fw={700}
                  c="neutral.6"
                  align="center"
                  style={{
                    whiteSpace: 'break-spaces' as const,
                  }}
                >
                  I&apos;ve already been rejected for financial aid
                </Text>
                <ArrowUpward color={config.content.rejectedButtonArrowColor} />
              </Flex>
            </Group>
          </Button>

          {/* Partnership-specific disclaimer */}
          {isPartnershipPreset && !isLessThan5k && (
            <Text
              size={13}
              c="primary.2"
              display="flex"
              fs="italic"
              mt={32}
            >
              <Text
                size={13}
                c="primary.2"
                fw={700}
                display="contents"
                ml={4}
              >
                EZ File&nbsp;
              </Text>
              will require you to disclose certain assets in order to determine eligibility. If
              you&apos;re unwilling to disclose your financial details to aid providers, you should
              continue with&nbsp;
              <Text
                size={13}
                c="primary.2"
                fw={700}
                display="contents"
                ml={4}
              >
                Resolve Concierge.
              </Text>
            </Text>
          )}
        </Flex>
      </Card>

      {/* Bottom Navigation Button */}
      {!isLessThan5k && (
        <Button
          type="button"
          onClick={handleBottomAction}
          mt={20}
          ml={10}
          mr={10}
          loading={isLoading}
          sx={{
            root: {
              backgroundColor: 'transparent',
              border: 'none',
            },
          }}
          p={0}
        >
          <Group spacing={2}>
            <>
              <Text
                mr={4}
                fw={700}
                c="primary.0"
              >
                {config.content.bottomButtonText}
              </Text>
              <ArrowUpward color={theme.colors.tertiary[1]} />
            </>
          </Group>
        </Button>
      )}
    </StepForm>
  );
};

export default FinancialAidStep;
